import { useEffect, useRef } from 'react';

/**
 * Fetch the next page when the end of a list scrolls into view.
 *
 * Returns a ref to hang on a sentinel element placed AFTER the last row. An
 * IntersectionObserver rather than a scroll handler on purpose: the element
 * that actually scrolls differs per list — the conversation rail, the calls
 * pane, the campaigns page — and an observer finds it by itself, where a scroll
 * handler needs each caller to name the right ancestor and goes quietly dead
 * the day one of them is restyled.
 *
 * `rootMargin` starts the fetch before the sentinel is on screen, so a list
 * read at a normal speed never shows its own bottom edge.
 *
 * Nothing debounces the callback: the observer fires again on every scroll that
 * keeps the sentinel in view, and it STAYS in view while the new rows are in
 * flight. `busy` is what stops the second call, which is why it has to be the
 * caller's real in-flight flag and not a page counter — a list that pages while
 * `busy` is still false asks for the same page twice.
 *
 * Callers keep their Load more button. This is an accelerator, not the only way
 * down a list: an observer that never fires (a hidden tab, a browser that
 * throttles it, a container with no height yet) must not be able to strand
 * someone half way through their campaigns.
 */
export function useInfiniteScroll({
  hasMore,
  busy,
  onLoadMore,
  rootMargin = '240px',
}: {
  hasMore: boolean;
  busy: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  // Read through a ref so a callback that changes identity every render — most
  // of them do — does not tear the observer down and rebuild it every time.
  const load = useRef(onLoadMore);
  useEffect(() => {
    load.current = onLoadMore;
  });

  useEffect(() => {
    const node = sentinel.current;
    // Disconnected while a page is in flight, and re-connected when it lands:
    // if the sentinel is still visible then (a short page, a tall window) that
    // re-observe is what fetches the one after it.
    if (!node || !hasMore || busy) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) load.current();
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, busy, rootMargin]);

  return sentinel;
}
